import { Component } from '@theme/component';

/**
 * A progress bar for the carousel above it.
 *
 * It exists because Horizon draws none: a `product-list` carousel offers
 * arrows or nothing, and the design asks for a rule under the strip whose
 * filled part is the share of it you can see.
 *
 * The bar reads `scrollLeft` off the slideshow rather than its slide index.
 * The element is `overflow-x: hidden`, but Horizon moves it by scrolling, so
 * the scroll position is real and continuous -- which is what lets the bar
 * follow a drag instead of jumping a slide at a time.
 *
 * @extends {Component}
 */
class MmCarouselProgressComponent extends Component {
  requiredRefs = ['thumb'];

  connectedCallback() {
    super.connectedCallback();
    this.#connectToCarousel();
  }

  disconnectedCallback() {
    this.#detach();
    super.disconnectedCallback?.();
  }

  /** The section can be re-rendered with a different number of products. */
  updatedCallback() {
    this.#detach();
    this.#connectToCarousel();
  }

  #connectToCarousel() {
    // The bar renders after the list inside the same section, so the carousel
    // is a sibling subtree rather than an ancestor: the section is the scope.
    const scope = this.closest('.shopify-section') ?? document;

    // Scoped to the carousel and filtered past the cards. Every product card
    // carries its own gallery, which is also a <slideshow-slides>, so a bare
    // query for the tag inside this section returns the galleries too -- and
    // the first one with a box was a 124px card image, not the strip.
    const candidates = [...scope.querySelectorAll('.resource-list__carousel slideshow-slides')].filter(
      (el) => !el.closest('.product-card')
    );

    // A section can hold two copies of the strip, one per breakpoint, and only
    // one of them has a box at any time.
    this.#track =
      candidates.find((el) => el.getBoundingClientRect().width > 0) ?? candidates[0] ?? null;

    if (!this.#track) {
      this.hidden = true;
      return;
    }

    this.#onScroll = () => this.#sync();
    this.#track.addEventListener('scroll', this.#onScroll, { passive: true });

    // A drag does not always land as a scroll event: the slideshow captures the
    // pointer itself and announces the result with its own event, which is the
    // one that carries trigger 'drag'. Listening to both keeps the bar moving
    // during the gesture and correct once it settles.
    this.#slideshow = this.#track.closest('slideshow-component') ?? this.#track.parentElement;
    this.#onSelect = () => this.#sync();
    this.#slideshow?.addEventListener('slideshowSelect', this.#onSelect);
    this.#track.addEventListener('pointermove', this.#onSelect, { passive: true });

    this.#observer = new ResizeObserver(() => this.#sync());
    this.#observer.observe(this.#track);

    this.#sync();
  }

  #detach() {
    this.#track?.removeEventListener('scroll', this.#onScroll);
    this.#track?.removeEventListener('pointermove', this.#onSelect);
    this.#slideshow?.removeEventListener('slideshowSelect', this.#onSelect);
    this.#observer?.disconnect();
    this.#track = null;
    this.#slideshow = null;
  }

  #sync() {
    const track = this.#track;
    if (!track) return;

    const { scrollWidth, clientWidth, scrollLeft } = track;
    const scrollable = scrollWidth - clientWidth;

    // Nothing to scroll, nothing to indicate.
    if (scrollable <= 1) {
      this.hidden = true;
      return;
    }
    this.hidden = false;

    const visibleShare = clientWidth / scrollWidth;
    const progress = scrollLeft / scrollable;

    this.style.setProperty('--mm-progress-width', `${visibleShare * 100}%`);
    // Travel is the empty part of the rail, so the thumb lands flush at both
    // ends instead of overshooting.
    this.style.setProperty('--mm-progress-offset', `${progress * (1 - visibleShare) * 100}%`);
  }

  /** @type {Element | null} */
  #track = null;
  /** @type {ResizeObserver | undefined} */
  #observer;
  /** @type {Element | null} */
  #slideshow = null;
  /** @type {() => void} */
  #onScroll = () => {};
  /** @type {() => void} */
  #onSelect = () => {};
}

if (!customElements.get('mm-carousel-progress-component')) {
  customElements.define('mm-carousel-progress-component', MmCarouselProgressComponent);
}
